/**
 * Spoken words through the browser's own voices (speechSynthesis): the street chatter and Sprawl FM's presenters.
 * One place picks voices so a speaker sounds the same every time, and one queue keeps the station from talking over
 * a passer-by (the station wins: it cancels chatter; chatter waits for the station).
 * Later, pre-rendered clips can take the place of any utterance (see docs/DESIGN_STATION.md).
 */
export interface Voice { sex: 'f' | 'm'; pitch: number; rate: number; /** stable pick among the voices of that sex */ seed: number }
const FEMALE = /female|woman|samantha|victoria|karen|moira|tessa|zira|susan|fiona|allison|ava|kate|serena|hazel|zoe|aria|jenny|emma|olivia|amy|salli|joanna|kimberly|ivy|nicole|raveena|libby|sonia|natasha|catherine|female/i;
const MALE = /male|man\b|daniel|alex\b|fred|david|mark|george|james|guy|ryan|brian|matthew|joey|justin|kevin|arthur|oliver|liam|william|thomas|rishi|christopher|eric|andrew|roger|steffan/i;

export class Speech {
  private voices: SpeechSynthesisVoice[] = [];
  private bySex: Record<'f' | 'm', SpeechSynthesisVoice[]> = { f: [], m: [] };
  private busyUntil = 0;
  private priorityUntil = 0;
  enabled = true;
  constructor() {
    const synth = typeof speechSynthesis !== 'undefined' ? speechSynthesis : undefined;
    if (!synth) return;
    const load = () => {
      const all = synth.getVoices();
      this.voices = all.filter((v) => v.lang.toLowerCase().startsWith('en')); if (!this.voices.length) this.voices = all;
      this.bySex = { f: this.voices.filter((v) => FEMALE.test(v.name) && !MALE.test(v.name)), m: this.voices.filter((v) => MALE.test(v.name) && !FEMALE.test(v.name)) };
      // a browser that names nothing: split the list in two so the sexes at least differ
      if (!this.bySex.f.length && this.voices.length > 1) this.bySex.f = this.voices.filter((_, i) => i % 2 === 1);
      if (!this.bySex.m.length && this.voices.length) this.bySex.m = this.voices.filter((_, i) => i % 2 === 0);
    };
    load(); synth.addEventListener?.('voiceschanged', load);
  }
  get available(): boolean { return typeof speechSynthesis !== 'undefined'; }
  /** something is being said (or queued) right now */
  get busy(): boolean { return performance.now() / 1000 < this.busyUntil; }
  /**
   * Say it. `priority` (the station) cancels whatever is playing; without it the call is dropped while something else
   * talks. Returns the seconds it will take (estimated), 0 if not spoken.
   */
  say(text: string, v: Voice, volume = 1, priority = false): number {
    const synth = typeof speechSynthesis !== 'undefined' ? speechSynthesis : undefined;
    if (!synth || !this.enabled || volume <= 0 || !text.trim()) return 0;
    const now = performance.now() / 1000;
    if (!priority && (this.busy || now < this.priorityUntil)) return 0;
    if (priority || now - this.busyUntil > 10) synth.cancel(); // (Chrome can wedge with speaking=true after a tab switch: clear it)
    try {
      const u = new SpeechSynthesisUtterance(text);
      const pool = this.bySex[v.sex].length ? this.bySex[v.sex] : this.voices;
      if (pool.length) u.voice = pool[Math.abs(v.seed) % pool.length];
      u.pitch = Math.max(0.5, Math.min(2, v.pitch)); u.rate = Math.max(0.5, Math.min(2, v.rate)); u.volume = Math.min(1, volume);
      const secs = Math.min(30, 0.5 + text.length * 0.065 / u.rate);
      this.busyUntil = now + secs; if (priority) this.priorityUntil = now + secs + 0.5;
      u.onend = () => { this.busyUntil = Math.min(this.busyUntil, performance.now() / 1000); };
      synth.speak(u);
      return secs;
    } catch { return 0; }
  }
  stop(): void { try { speechSynthesis?.cancel(); } catch { /* none */ } this.busyUntil = 0; this.priorityUntil = 0; }
}
export const speech = new Speech();
